import React from 'react';
import { Flex } from 'lib/components/styles/layout';
import { BiChevronUp, BiChevronDown } from 'react-icons/bi';
import { CardManageAccess } from './CardManageAccess';
import { FolderAccessSettings, OemCardItem, RowData } from './types';
import { AccessOptions } from 'lib/api/folderAccess/types';
import styled, { useTheme } from 'styled-components/macro';
import { CommonTypography } from 'lib/components/styles/typography';
import { OemCardManageAccess } from './OemCardManageAcess';
import { useTranslation } from 'react-i18next';

type Props = {
  expandAccessSection: boolean;
  accessList: FolderAccessSettings[];
  accessOptions: AccessOptions[];
  setAccessList: React.Dispatch<React.SetStateAction<FolderAccessSettings[]>>;
  setExpandAccessSection: (value: boolean) => void;
  forOem?: boolean;
};

export const Heading = styled(CommonTypography).attrs({ as: 'h1' })`
  font-weight: 800;
  font-size: 20px;
  line-height: 40px;
`;

export const ManageAccessSection = ({
  expandAccessSection,
  accessList,
  accessOptions,
  setAccessList,
  setExpandAccessSection,
  forOem = false,
}: Props) => {
  const themes = useTheme();
  const { t } = useTranslation();

  const groupedAccessData = React.useMemo(() => {
    if (!forOem) {
      return [];
    }

    const grouped: Record<number, OemCardItem> = {};

    accessList.forEach(item => {
      if (item.isDeleted) {
        return;
      }

      const roleId = item.targetAutomotiveRoleId || 0;
      if (!grouped[roleId]) {
        grouped[roleId] = {
          id: item.targetAutomotiveRoleId || 0,
          name: item.targetAutomotiveRoleName || '',
          isReadOnly: !!item.isReadOnly,
          isDeleted: !!item.isDeleted,
          rowData: [] as RowData[],
        };
      }

      const baseRow = {
        access: item.access || '',
        targetOrganizationId: item.targetOrganizationId,
        targetOrganizationName: item.targetOrganizationName,
      };

      const location =
        'targetRegionId' in item && !!item.targetRegionId
          ? {
              targetRegionId: item.targetRegionId,
              targetRegionName: item.targetRegionName,
            }
          : {
              targetCustomerId: item.targetCustomerId,
              targetCustomerName: item.targetCustomerName,
            };

      grouped[roleId].rowData.push({ ...baseRow, ...location });
    });

    return Object.values(grouped); // Convert object to array
  }, [accessList, forOem]);

  return (
    <Flex>
      <Flex
        flexDirection='row'
        alignItems='center'
        backgroundColor={themes.colors.neutral[10]}
        justifyContent='space-between'
        padding='0px 20px 0px 23px'
        cursor='pointer'
        borderRadius='6px'
        height='64px'
        width=''
        style={{
          maxWidth: '798px',
        }}
        onClick={() => setExpandAccessSection(!expandAccessSection)}
      >
        <Heading>{t('Who Can Access')}</Heading>
        {expandAccessSection ? (
          <BiChevronUp size='24' />
        ) : (
          <BiChevronDown size='24' />
        )}
      </Flex>
      {!!expandAccessSection && (
        <Flex gap='6px' padding='6px 0px 0px 0px'>
          {forOem
            ? groupedAccessData.map((group, index) => (
                <OemCardManageAccess
                  key={group.id}
                  item={group}
                  accessOptionsData={accessOptions}
                  setAccessData={setAccessList}
                  isLastItem={index === groupedAccessData.length - 1}
                />
              ))
            : accessList.map((item, index) => {
                if (item.isDeleted) {
                  return null;
                }
                return (
                  <CardManageAccess
                    key={index}
                    item={item}
                    accessOptionsData={accessOptions}
                    setAccessData={setAccessList}
                    isLastItem={index === accessList.length - 1}
                  />
                );
              })}
        </Flex>
      )}
    </Flex>
  );
};
